/**
 * GlobalSearchPalette — Phase 1B-J (2026-05-30).
 *
 * Cmd/Ctrl+K command-palette overlay. Hits /api/v1/search with the
 * operator's query, groups results by kind (Sessions / Evidence),
 * arrow-key navigation, Enter to jump, Esc to close.
 *
 * Lives at app-root level (mounted from App.tsx via
 * <GlobalSearchPaletteHost />) so any page in the SPA can summon it
 * without per-route plumbing.
 */
import { useQuery } from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ApiPaths, api } from "@/lib/api";


export type GlobalSearchResult = {
  kind: "session" | "evidence";
  id: string;
  title: string;
  snippet: string;
  created_at: string;
  session_id: string;
  href: string;
};


type GlobalSearchResponse = {
  results: GlobalSearchResult[];
  query: string;
  kind: string;
};


const DEBOUNCE_MS = 200;
const MIN_QUERY_LEN = 2;


export function GlobalSearchPalette({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [draft, setDraft] = useState("");
  const [debounced, setDebounced] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Debounce the query so we don't spam the backend on every keystroke.
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(draft.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [draft]);

  // Reset state every time the palette opens.
  useEffect(() => {
    if (!open) return;
    setDraft("");
    setDebounced("");
    setActiveIndex(0);
    queueMicrotask(() => inputRef.current?.focus());
  }, [open]);

  const search = useQuery({
    queryKey: ["global-search", debounced],
    enabled: open && debounced.length >= MIN_QUERY_LEN,
    queryFn: async () => {
      const res = await api.get<GlobalSearchResponse>(ApiPaths.globalSearch, {
        params: { q: debounced, kind: "all", limit: 20 },
      });
      return res.data;
    },
    staleTime: 30_000,
  });

  // Two-section grouping (Sessions then Evidence) so the list reads
  // top-to-bottom with kind headers. The `search.data?.results ?? []`
  // null-coalesce lives INSIDE the useMemo so the fallback empty
  // array isn't a fresh reference on every render — react-hooks/
  // exhaustive-deps would otherwise re-fire the useMemo every render.
  const grouped = useMemo(() => {
    const results = search.data?.results ?? [];
    const sessions = results.filter((r) => r.kind === "session");
    const evidence = results.filter((r) => r.kind === "evidence");
    // Flat array we navigate with arrows; headers don't take focus.
    const flat = [...sessions, ...evidence];
    return { sessions, evidence, flat };
  }, [search.data?.results]);

  // Clamp activeIndex when the flat list shrinks.
  useEffect(() => {
    if (activeIndex >= grouped.flat.length) {
      setActiveIndex(Math.max(0, grouped.flat.length - 1));
    }
  }, [grouped.flat.length, activeIndex]);

  const onSelect = useCallback(
    (r: GlobalSearchResult) => {
      onClose();
      navigate(r.href);
    },
    [navigate, onClose],
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) =>
        grouped.flat.length === 0 ? 0 : (i + 1) % grouped.flat.length,
      );
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) =>
        grouped.flat.length === 0
          ? 0
          : (i - 1 + grouped.flat.length) % grouped.flat.length,
      );
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const pick = grouped.flat[activeIndex];
      if (pick) onSelect(pick);
    }
  };

  if (!open) return null;

  const showEmptyHint = debounced.length < MIN_QUERY_LEN;
  const showNoResults =
    !showEmptyHint && !search.isPending && grouped.flat.length === 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("ui.globalSearch.dialogLabel")}
      className="fixed inset-0 z-[200] flex items-start justify-center bg-black/60 p-4 pt-[10vh]"
      onClick={onClose}
      onKeyDown={onKeyDown}
    >
      <div
        className="w-full max-w-2xl overflow-hidden rounded-2xl border border-nb-border bg-nb-bg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-nb-border px-4 py-3">
          <SearchIcon />
          <input
            ref={inputRef}
            type="text"
            placeholder={t("ui.globalSearch.placeholder")}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="flex-1 bg-transparent text-base text-nb-text placeholder:text-nb-text-3 focus:outline-none"
            data-testid="global-search-input"
          />
          <kbd className="rounded border border-nb-border px-1.5 py-0.5 text-[10px] text-nb-text-3">
            Esc
          </kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {showEmptyHint && (
            <div className="px-4 py-8 text-center text-sm text-nb-text-3">
              {t("ui.globalSearch.minLenHint")}
            </div>
          )}
          {showNoResults && (
            <div className="px-4 py-8 text-center text-sm text-nb-text-3">
              <Trans
                i18nKey="ui.globalSearch.noMatches"
                values={{ query: debounced }}
                components={{ strong: <strong /> }}
              />
            </div>
          )}
          {grouped.sessions.length > 0 && (
            <Section title={t("ui.globalSearch.sectionSessions")}>
              {grouped.sessions.map((r, i) => (
                <Row
                  key={r.id}
                  result={r}
                  active={i === activeIndex}
                  onClick={() => onSelect(r)}
                  onHover={() => setActiveIndex(i)}
                />
              ))}
            </Section>
          )}
          {grouped.evidence.length > 0 && (
            <Section title={t("ui.globalSearch.sectionEvidence")}>
              {grouped.evidence.map((r, i) => {
                const flatIdx = grouped.sessions.length + i;
                return (
                  <Row
                    key={r.id}
                    result={r}
                    active={flatIdx === activeIndex}
                    onClick={() => onSelect(r)}
                    onHover={() => setActiveIndex(flatIdx)}
                  />
                );
              })}
            </Section>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-nb-border bg-nb-bg/80 px-4 py-2 text-[11px] text-nb-text-3">
          <span>
            <kbd className="mr-1 rounded border border-nb-border px-1.5 py-0.5">
              ↑
            </kbd>
            <kbd className="mr-2 rounded border border-nb-border px-1.5 py-0.5">
              ↓
            </kbd>
            {t("ui.globalSearch.navigate")}
            <span className="mx-2">·</span>
            <kbd className="mr-1 rounded border border-nb-border px-1.5 py-0.5">
              ↵
            </kbd>
            {t("ui.globalSearch.open")}
          </span>
          {search.isFetching && <span>{t("ui.globalSearch.searching")}</span>}
        </div>
      </div>
    </div>
  );
}


function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="px-4 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-nb-text-3">
        {title}
      </h3>
      <ul>{children}</ul>
    </div>
  );
}


function Row({
  result,
  active,
  onClick,
  onHover,
}: {
  result: GlobalSearchResult;
  active: boolean;
  onClick: () => void;
  onHover: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        onMouseEnter={onHover}
        className={`flex w-full items-center gap-3 px-4 py-2 text-left transition ${
          active ? "bg-nb-surface" : "hover:bg-nb-surface/60"
        }`}
        data-testid="global-search-row"
        data-active={active || undefined}
      >
        <span
          aria-hidden
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-nb-mint/15 text-base"
        >
          {result.kind === "session" ? "🎥" : "📁"}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-nb-text">
            {result.title}
          </span>
          <span className="block truncate text-[11px] text-nb-text-3">
            {result.snippet}
          </span>
        </span>
        <span className="shrink-0 text-nb-mint" aria-hidden>
          →
        </span>
      </button>
    </li>
  );
}


function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className="h-4 w-4 text-nb-text-3"
      aria-hidden
    >
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
      <path
        d="M20 20l-3-3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}


/** Host component — owns the open/closed state + the Cmd/Ctrl+K
 *  hotkey. Mount once at app root. */
export function GlobalSearchPaletteHost() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return <GlobalSearchPalette open={open} onClose={() => setOpen(false)} />;
}
